/**
 * Massager Page
 *
 * マッサージ師/はり・きゅう師 - マスタメンテナンス
 * Display and manage massager/acupuncturist information.
 */

import { AppSpin } from '@/components/atoms';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import {
  MassagerBulkSettingsModal,
  MassagerDetailModal,
  MassagerListSection,
} from '@/components/organisms';
import {
  useMainMassagerBulkUpdateAlert,
  useMainMassagerExportCsv,
  useMainMassagerInit,
  useMainMassagerSearch,
} from '@/hooks/main/useMainMassager';
import { useMassagerPageFormGuard } from '@/hooks/main/useMassagerPageFormGuard';
import useAppToast from '@/hooks/useAppToast';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { useEffect, useMemo, useState } from 'react';
import { getDisplayTypeLabel } from './constants';
import {
  BulkUpdateAlertRequest,
  MassagerClinicLocation,
  MassagerSearchFilters,
  MassagerSearchPaginatedData,
  PaginationInfo,
} from './types';

/**
 * Main Massager Page Component
 */
const MassagerPage: React.FC = () => {
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;

  // Initialize centralized form guard for both modals
  useMassagerPageFormGuard();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Clear selected items and modals when service changes
  // Also reset pagination + sorting to default state
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedMassagerId(null);
    setDetailModalOpen(false);
    setBulkSettingsModalOpen(false);
    setFilters((prev: MassagerSearchFilters) => ({
      ...prev,
      page: 1,
      sort_field: undefined,
      sort_order: undefined,
    }));
  }, [serviceType]);

  const [selectedMassagerId, setSelectedMassagerId] = useState<number | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [bulkSettingsModalOpen, setBulkSettingsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState<MassagerSearchFilters>({
    page_num: 20,
    // No sort_type → BE defaults to massagers.id ASC
  });

  const searchParams = useMemo(() => filters, [filters]);

  const { data: initResponse } = useMainMassagerInit();
  const {
    data: searchResponse,
    isLoading,
    isFetching,
    error: searchError,
  } = useMainMassagerSearch(searchParams);

  const { exportCsvMutation, isLoading: exportCsvPending } = useMainMassagerExportCsv();
  const { bulkUpdateMutation } = useMainMassagerBulkUpdateAlert();

  const checkinEnabled = initResponse?.data?.checkin_enabled ?? false;
  // keep last successful response visible while fetching so we don't cause layout shift
  const [visibleMassagers, setVisibleMassagers] = useState<MassagerClinicLocation[]>([]);
  const [visiblePagination, setVisiblePagination] = useState<PaginationInfo | null>(null);

  // update visible cache whenever we get a successful response
  useEffect(() => {
    if (searchResponse?.success && searchResponse.data) {
      if (
        typeof searchResponse.data === 'object' &&
        'data' in searchResponse.data &&
        'pagination' in searchResponse.data
      ) {
        const paginatedData = searchResponse.data as MassagerSearchPaginatedData;
        setVisibleMassagers(paginatedData.data);
        setVisiblePagination(paginatedData.pagination);
      } else {
        setVisibleMassagers(searchResponse.data as MassagerClinicLocation[]);
        setVisiblePagination(null);
      }
    }
  }, [searchResponse]);

  // render from the visible cache (so refetching doesn't clear the UI)
  const massagers = visibleMassagers;
  const pagination = visiblePagination;

  useEffect(() => {
    if (searchError) setError('データの取得に失敗しました。');
  }, [searchError]);

  const handlePageChange = (page: number) => {
    setFilters((prev: MassagerSearchFilters) => ({ ...prev, page }));
  };

  const handleSortChange = (field: string | null, order: 'asc' | 'desc' | null) => {
    setFilters(prev => ({
      ...prev,
      sort_field: field || undefined,
      sort_order: order || undefined,
    }));
  };

  const handleCheckboxToggle = (id: number) => {
    // Find the massager_id for the given clinic_location_massagers id
    const targetItem = massagers.find((m: MassagerClinicLocation) => m.id === id);
    if (!targetItem) return;

    const targetMassagerId = targetItem.massager_id;

    // Find all ids in the current view that share the same massager_id
    const relatedIds = massagers
      .filter((m: MassagerClinicLocation) => m.massager_id === targetMassagerId)
      .map((m: MassagerClinicLocation) => m.id);

    setSelectedIds((prev: Set<number>) => {
      const next = new Set(prev);
      const isSelecting = !next.has(id);

      if (isSelecting) {
        relatedIds.forEach((relatedId: number) => next.add(relatedId));
      } else {
        relatedIds.forEach((relatedId: number) => next.delete(relatedId));
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === massagers.length
        ? new Set()
        : new Set(massagers.map((m: MassagerClinicLocation) => m.id)) // CLM id
    );
  };

  const handleDetailClick = (massager: MassagerClinicLocation) => {
    setSelectedMassagerId(massager.id); // CLM id
    setDetailModalOpen(true);
  };

  const toast = useAppToast();

  const handleExportCsv = async () => {
    try {
      await exportCsvMutation(searchParams);
    } catch (e: any) {
      // Let the global network error handler show its toast if it's a network error
      const isOffline = typeof window !== 'undefined' && !window.navigator.onLine;
      const isAxiosNetworkError = e?.code === 'ERR_NETWORK' || e?.message === 'Network Error';
      if (!isOffline && !isAxiosNetworkError) {
        toast.error('CSV出力に失敗しました。');
      }
      // Clear the inline error just in case
      setError(null);
    }
  };

  const handleBulkUpdate = (data: BulkUpdateAlertRequest) => {
    return bulkUpdateMutation(data);
  };

  const pageTitle = getDisplayTypeLabel(serviceType);
  const numberColLabel = '施術師番号';
  const nameColLabel = '施術師名';
  const addressColLabel = '拠点住所';

  /** Full-page spinner when search runs and list cache is still empty (first load / filter cleared). */

  if (isLoading) {
    return (
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80"
        aria-busy="true"
      >
        <AppSpin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative min-h-[200px]">
      <MassagerListSection
        error={error}
        successMessage={successMessage}
        onDismissSuccess={() => setSuccessMessage(null)}
        checkinEnabled={checkinEnabled}
        serviceType={serviceType}
        massagers={massagers}
        pagination={pagination}
        selectedIds={selectedIds}
        onPageChange={handlePageChange}
        sortField={filters.sort_field}
        sortOrder={filters.sort_order}
        onSortChange={handleSortChange}
        onCheckboxToggle={handleCheckboxToggle}
        onSelectAll={handleSelectAll}
        onDetailClick={handleDetailClick}
        onExportCsv={handleExportCsv}
        onOpenBulkSettings={() => setBulkSettingsModalOpen(true)}
        exportCsvPending={exportCsvPending}
        loading={isFetching}
        isA2={isA2}
        numberColLabel={numberColLabel}
        nameColLabel={nameColLabel}
        addressColLabel={addressColLabel}
      />

      <MassagerDetailModal
        massagerId={selectedMassagerId}
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedMassagerId(null);
        }}
        isA2={isA2}
        checkinEnabled={checkinEnabled}
      />

      <MassagerBulkSettingsModal
        isOpen={bulkSettingsModalOpen}
        onClose={() => setBulkSettingsModalOpen(false)}
        onSubmit={handleBulkUpdate}
        onSuccess={() => setSelectedIds(new Set())}
        selectedCount={selectedIds.size}
        selectedIds={Array.from(selectedIds)}
        displayType={pageTitle}
      />
    </div>
  );
};

export default MassagerPage;
